import {z} from 'zod';

// Use interfaces (not type aliases) for the recursive cases so the type is
// instantiated lazily — type aliases caused TS2589 "type instantiation is
// excessively deep and possibly infinite" inside immer's `Draft<T>`
// expansion in the IX3 store reducer.
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonArray
  | JsonRecord;
export interface JsonArray extends Array<JsonValue> {}
export interface JsonRecord {
  [k: string]: JsonValue;
}

export const unsafeJsonRecordKeys: ReadonlySet<string> = new Set([
  '__proto__',
  'constructor',
  'prototype',
]);
const MAX_JSON_METADATA_DEPTH = 16;
const MAX_JSON_METADATA_NODES = 1000;
const MAX_JSON_METADATA_STRING_LENGTH = 16_384;
// Total approximate serialized size cap. Per-string and per-node caps alone
// allow up to ~16 MB (1000 nodes × 16 KB strings); 64 KB keeps a single
// `pluginConfig` well within Mongo Mixed budgets.
const MAX_JSON_METADATA_TOTAL_BYTES = 65_536;
const MAX_PLUGIN_TYPE_LENGTH = 64;

/**
 * Byte budget for the open `z.unknown()` value fields the schemas leave
 * unbounded — target values, `filterContext.filterBy` values, condition
 * values, and animation property values. Matches `pluginConfig`'s per-bag cap.
 *
 * Not a single global budget: `pluginConfig` bags are each capped separately by
 * `pluginConfigSchema`, so they are NOT folded into this one. Many bags on one
 * interaction can still sum past this cap in aggregate — tracked as a
 * before-external follow-up, out of this guard's open-`unknown` slice.
 */
export const MAX_IX3_VALUE_TOTAL_BYTES = MAX_JSON_METADATA_TOTAL_BYTES;

/**
 * Running byte budget for the open `z.unknown()` value fields of ONE
 * interaction — target values, `filterContext.filterBy` values, condition
 * values, and animation property values (and names). `add()` is called once
 * per value and BYTES carry across calls, so many sibling values that are each
 * under the cap still fail in aggregate (the published-payload risk is the
 * total, not any one value).
 *
 * SIZE, not SHAPE. It measures approximate serialized bytes and, per value,
 * bounds nesting depth, node count, per-string length, and rejects cycles — the
 * DoS-shaped limits from `validateJsonMetadataBounds`. It deliberately does NOT
 * enforce that guard's *shape* rules (plain-object-only, unsafe-key, finite
 * numbers): the `unknown` is legitimate for plugin flexibility, and `undefined`
 * costs nothing rather than raising so sparse animation forms like `[null, to]`
 * stay legal. Node count, depth, and cycles are per-value (reset in `add()`);
 * only bytes accumulate. Returns the first violation string, or `null`.
 *
 * Iterative (no recursion) so a deeply nested value can't blow the call stack
 * before the depth cap catches it.
 */
export function createIx3ValueBudget(
  maxBytes: number = MAX_IX3_VALUE_TOTAL_BYTES
) {
  // Bytes are interaction-wide (the published-payload risk is the aggregate).
  // Node count, depth, and cycles are per-value structure — a property-dense
  // but small interaction (many `[null, to]` values) is legitimate, so the
  // 1000-node cap must apply to each value, not the whole interaction. It is
  // the wide-container DoS guard, not a limit on how many fields you can have.
  let bytes = 0;

  const add = (value: unknown, where: string): string | null => {
    // `undefined` is a legal absent value here and adds nothing to walk.
    if (value === undefined) return null;

    let nodes = 0;
    const seen = new WeakSet<object>();
    const stack: Array<{current: unknown; depth: number}> = [
      {current: value, depth: 0},
    ];

    while (stack.length > 0) {
      const {current, depth} = stack.pop()!;

      if (depth > MAX_JSON_METADATA_DEPTH) {
        return `${where} exceeds the maximum nesting depth of ${MAX_JSON_METADATA_DEPTH}.`;
      }

      nodes += 1;
      if (nodes > MAX_JSON_METADATA_NODES) {
        return `${where} exceeds the maximum of ${MAX_JSON_METADATA_NODES} values in one field.`;
      }

      // Charge the serialized structure, not just leaf data: `null`,
      // container brackets, quotes, and element separators all take bytes on
      // the published payload. Otherwise 999 `null`s in a value score ~0 bytes
      // yet serialize to kilobytes, and enough of them across an interaction
      // reach megabytes while staying under both the byte and per-value node
      // caps. Costs are approximate `JSON.stringify` sizes, not exact.
      switch (typeof current) {
        case 'string':
          if (current.length > MAX_JSON_METADATA_STRING_LENGTH) {
            return `${where} contains a string longer than ${MAX_JSON_METADATA_STRING_LENGTH} characters.`;
          }
          bytes += current.length + 2; // surrounding quotes
          break;
        case 'number':
          bytes += 8;
          break;
        case 'boolean':
          bytes += 5; // worst case "false"
          break;
        case 'object': {
          if (current === null) {
            bytes += 4; // "null"
            break;
          }
          if (seen.has(current)) {
            return `${where} contains a circular reference.`;
          }
          seen.add(current);
          if (Array.isArray(current)) {
            // brackets + one separator per element beyond the first
            bytes += 2 + Math.max(0, current.length - 1);
            for (const item of current)
              stack.push({current: item, depth: depth + 1});
          } else {
            const entries = Object.entries(current);
            // braces + one separator per entry beyond the first
            bytes += 2 + Math.max(0, entries.length - 1);
            for (const [key, nested] of entries) {
              // Serialized as `"key":` — quotes + colon on top of the key.
              bytes += key.length + 3;
              stack.push({current: nested, depth: depth + 1});
            }
          }
          break;
        }
        // Functions/symbols aren't JSON and can't survive the postMessage hop;
        // ignored rather than rejected — shape is `parseIX3DataStrict`'s job.
        default:
          break;
      }

      if (bytes > maxBytes) {
        return `${where} exceeds the maximum of ${maxBytes} bytes.`;
      }
    }

    return null;
  };

  return {add};
}

type WalkFrame = {
  current: unknown;
  depth: number;
  parent: WalkFrame | null;
  key: string | number | null;
};

function framePath(frame: WalkFrame): Array<string | number> {
  const path: Array<string | number> = [];
  let cursor: WalkFrame | null = frame;
  while (cursor && cursor.key !== null) {
    path.unshift(cursor.key);
    cursor = cursor.parent;
  }
  return path;
}

/**
 * Iteratively walks a JSON-safe structure (no recursion, so deeply nested
 * input cannot blow the call stack) and enforces:
 *  - depth ≤ MAX_JSON_METADATA_DEPTH
 *  - total node count ≤ MAX_JSON_METADATA_NODES
 *  - total approximate bytes ≤ MAX_JSON_METADATA_TOTAL_BYTES
 *  - per-string length ≤ MAX_JSON_METADATA_STRING_LENGTH
 *  - finite numbers only
 *  - no `__proto__` / `constructor` / `prototype` keys
 *  - plain objects only (Object.prototype or null prototype)
 *  - no circular references (defense-in-depth alongside the depth cap)
 *
 * Paths are materialized lazily via parent pointers, so the success path
 * allocates one small frame per node instead of a fresh path array on every
 * push.
 */
function validateJsonMetadataBounds(
  value: unknown,
  ctx: z.RefinementCtx
): void {
  let nodes = 0;
  let bytes = 0;
  const seenObjects = new WeakSet<object>();
  const stack: WalkFrame[] = [
    {current: value, depth: 0, parent: null, key: null},
  ];

  while (stack.length > 0) {
    const frame = stack.pop()!;
    const {current, depth} = frame;

    if (depth > MAX_JSON_METADATA_DEPTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `JSON metadata exceeds maximum JSON metadata depth of ${MAX_JSON_METADATA_DEPTH}`,
        path: framePath(frame),
      });
      return;
    }

    nodes += 1;
    if (nodes > MAX_JSON_METADATA_NODES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `JSON metadata exceeds maximum JSON metadata size of ${MAX_JSON_METADATA_NODES} nodes`,
        path: framePath(frame),
      });
      return;
    }

    switch (typeof current) {
      case 'string':
        if (current.length > MAX_JSON_METADATA_STRING_LENGTH) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `JSON metadata string exceeds maximum JSON metadata size of ${MAX_JSON_METADATA_STRING_LENGTH} characters`,
            path: framePath(frame),
          });
          return;
        }
        bytes += current.length;
        break;
      case 'number':
        if (!Number.isFinite(current)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'JSON metadata numbers must be finite',
            path: framePath(frame),
          });
          return;
        }
        bytes += 8;
        break;
      case 'boolean':
        bytes += 1;
        break;
      case 'undefined':
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'JSON metadata values must be JSON-serializable',
          path: framePath(frame),
        });
        return;
      case 'object': {
        if (current === null) break;

        if (seenObjects.has(current)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'JSON metadata cannot contain circular references',
            path: framePath(frame),
          });
          return;
        }
        seenObjects.add(current);

        if (Array.isArray(current)) {
          for (let index = current.length - 1; index >= 0; index--) {
            stack.push({
              current: current[index],
              depth: depth + 1,
              parent: frame,
              key: index,
            });
          }
          break;
        }

        const prototype = Object.getPrototypeOf(current);
        if (prototype !== Object.prototype && prototype !== null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'JSON metadata objects must be plain records',
            path: framePath(frame),
          });
          return;
        }

        const entries = Object.entries(current);
        for (let index = entries.length - 1; index >= 0; index--) {
          const [key, item] = entries[index]!;
          if (unsafeJsonRecordKeys.has(key)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Unsafe JSON record key "${key}"`,
              path: [...framePath(frame), key],
            });
            return;
          }
          bytes += key.length;
          stack.push({
            current: item,
            depth: depth + 1,
            parent: frame,
            key,
          });
        }
        break;
      }
      default:
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'JSON metadata values must be JSON-serializable',
          path: framePath(frame),
        });
        return;
    }

    if (bytes > MAX_JSON_METADATA_TOTAL_BYTES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `JSON metadata exceeds maximum total size of ${MAX_JSON_METADATA_TOTAL_BYTES} bytes`,
        path: framePath(frame),
      });
      return;
    }
  }
}

function validateJsonRecord(value: unknown, ctx: z.RefinementCtx): void {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'JSON metadata must be an object record',
    });
    return;
  }

  validateJsonMetadataBounds(value, ctx);
}

/**
 * JSON-safe record validated in a single pass by `validateJsonMetadataBounds`.
 * The earlier `.pipe(z.record(z.lazy(...)))` step was removed because it
 * re-walked the entire structure with no new checks — see PR #106940 review.
 */
export const jsonRecordSchema = z
  .unknown()
  .superRefine(validateJsonRecord) as z.ZodType<JsonRecord>;

/**
 * Plugin config bag. A JSON-safe record (see `jsonRecordSchema`) with an
 * optional `type` discriminator string capped at
 * `MAX_PLUGIN_TYPE_LENGTH` chars. Plugins MUST set `type` if they want to
 * recognize their own payload. Core stays ignorant of which `type` values are
 * valid; plugins narrow at their boundary.
 *
 * Unified name across all plugin extensibility points (trigger config,
 * trigger metadata, timeline action).
 */
export const pluginConfigSchema = jsonRecordSchema.superRefine(
  (record, ctx) => {
    // Chained `superRefine`s run independently — `validateJsonRecord` above
    // adds an issue for non-records but doesn't short-circuit, so guard here
    // before dereferencing. Without this, a null/non-object payload throws
    // "Cannot read properties of null (reading 'type')" on the cast below.
    if (record == null || typeof record !== 'object' || Array.isArray(record)) {
      return;
    }
    const typeValue = (record as {type?: unknown}).type;
    if (typeValue === undefined) return;
    if (typeof typeValue !== 'string') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'pluginConfig.type must be a string',
        path: ['type'],
      });
      return;
    }
    if (typeValue.length > MAX_PLUGIN_TYPE_LENGTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `pluginConfig.type must be at most ${MAX_PLUGIN_TYPE_LENGTH} characters`,
        path: ['type'],
      });
    }
  }
);
export type PluginConfig = z.infer<typeof pluginConfigSchema>;

/**
 * Extension key format using namespace:identifier pattern
 * @example "wf:click" | "finsweet:custom"
 */
export const extensionKeySchema = z
  .string()
  .regex(/^[a-zA-Z0-9-_]+:[a-zA-Z0-9-_]+$/, {
    message:
      'Extension key must be in format "namespace:identifier" (alphanumeric, hyphens, and underscores only)',
  }) as z.ZodType<`${string}:${string}`>;
export type ExtensionKey = z.infer<typeof extensionKeySchema>;

/**
 * Represents time values in seconds or milliseconds
 * @example 1.5 | "1500ms"
 */
export const secondsOrMsSchema = z
  .union([
    z.number().nonnegative(),
    z.string().regex(/^\d+(\.\d+)?ms$/, {
      message: 'Milliseconds must be in format "123ms" or "123.45ms"',
    }),
    z.null(),
  ])
  .transform((val) => {
    if (val == null) return 0.25;
    // Convert to consistent number representation (seconds)
    if (typeof val === 'number') return val;
    const ms = parseFloat(val.replace('ms', ''));
    return ms / 1000;
  }) as z.ZodType<number | `${number}ms`>;
export type SecondsOrMs = z.input<typeof secondsOrMsSchema>;
export type Duration = SecondsOrMs;

/**
 * MongoDB ObjectId validation
 * Accepts both ObjectId instances and valid 24-character hex strings
 */
export const objectIdSchema = z
  .string()
  .regex(/^[a-f0-9]{24}$/i, {message: 'Invalid ObjectId format'})
  .transform((val) => String(val)); // Always return a string for consistency

export type ObjectIdString = z.infer<typeof objectIdSchema>;

export const scopeSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('site'),
  }),
  z.object({
    type: z.literal('pages'),
    value: z.array(objectIdSchema),
  }),
  z.object({
    type: z.literal('component'),
    componentId: z.string(), // Component UUID
    libraryProfileId: z.string().optional(), // Stable library identifier
    variants: z.array(z.string()).optional(), // Variant option IDs; undefined = all variants
  }),
]);
export type Scope = z.infer<typeof scopeSchema>;

export const scopeTransforms = {
  /**
   * Converts a Mongoose document scope to a Scope type.
   * Handles legacy string format, object format, and edge cases.
   *
   * @param mongooseScope - The scope from Mongoose document (can be string or object)
   * @param pageId - The pageId from the interaction document (used for legacy string conversion)
   * @returns A valid Scope object
   */
  mongooseToScope(
    mongooseScope:
      | string
      | {
          type: string;
          value?: unknown[];
          componentId?: string;
          variants?: unknown[];
        }
      | null
      | undefined,
    pageId: string | {toString(): string}
  ): Scope {
    // Handle null/undefined - default to site
    if (!mongooseScope) {
      return {type: 'site'};
    }

    // Handle legacy string format
    if (typeof mongooseScope === 'string') {
      if (mongooseScope === 'site') {
        return {type: 'site'};
      }
      // Legacy non-site string becomes pages scope with current pageId
      return {type: 'pages', value: [String(pageId)]};
    }

    // Handle object format
    if (typeof mongooseScope === 'object' && mongooseScope !== null) {
      if (mongooseScope.type === 'site') {
        return {type: 'site'};
      }

      if (mongooseScope.type === 'component') {
        return {
          type: 'component',
          componentId: mongooseScope.componentId ?? '',
          ...(Array.isArray(mongooseScope.variants) &&
          mongooseScope.variants.length > 0
            ? {variants: mongooseScope.variants.map(String)}
            : {}),
        };
      }

      if (mongooseScope.type === 'pages') {
        return {
          type: 'pages',
          value: mongooseScope.value?.map(String) ?? [],
        };
      }
    }

    // Fallback to site if format is unrecognized
    return {type: 'site'};
  },
};
